import { useEffect, useRef, useState, type InputHTMLAttributes } from "react";
import "./drag-number-input.css";

type Props = Omit<InputHTMLAttributes<HTMLInputElement>, "type" | "value" | "defaultValue"> & {
  value: number | string;
  onValueChange: (value: number) => void;
  scrubFallbackValue?: number;
  "data-help"?: string;
};
type Gesture = {
  element: HTMLInputElement;
  pointerId: number;
  x: number;
  y: number;
  lastX: number;
  pendingX: number;
  remainder: number;
  value: number;
  step: number;
  min: number;
  max: number;
  active: boolean;
};

const finite = (value: unknown, fallback: number) => {
  if (value === "" || value == null) return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

/** A native number field with frame-paced, bounded horizontal scrubbing. */
export function DragNumberInput({
  value, onValueChange, scrubFallbackValue = 0, onChange, onBlur, onKeyDown,
  className = "", title, disabled, readOnly, "data-help": help, ...props
}: Props) {
  const gesture = useRef<Gesture | null>(null);
  const frame = useRef<number | null>(null);
  const suppressClick = useRef(false);
  const change = useRef(onValueChange);
  change.current = onValueChange;
  const [scrubbing, setScrubbing] = useState(false);

  const flush = () => {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const drag = gesture.current;
    if (!drag?.active) return;
    const distance = drag.pendingX - drag.lastX + drag.remainder;
    const steps = Math.trunc(distance / 8);
    drag.lastX = drag.pendingX;
    drag.remainder = distance - steps * 8;
    if ((drag.value === drag.max && distance > 0) || (drag.value === drag.min && distance < 0)) {
      drag.remainder = 0;
      return;
    }
    if (!steps) return;
    const raw = drag.value + steps * drag.step;
    const next = Math.max(drag.min, Math.min(drag.max, Number(raw.toFixed(8))));
    // Discard overshoot at a bound so reversing responds immediately.
    if (raw <= drag.min || raw >= drag.max) drag.remainder = 0;
    if (next === drag.value) return;
    drag.value = next;
    change.current(next);
  };

  const updatePendingPoint = (x: number) => {
    const drag = gesture.current;
    if (!drag?.active) return;
    // Preserve a rapid reversal at a bound even when both pointer samples
    // arrive in the same animation frame. Coalesce delivery, not direction.
    const pendingDirection = Math.sign(drag.pendingX - drag.lastX);
    const nextDirection = Math.sign(x - drag.pendingX);
    if (pendingDirection && nextDirection && pendingDirection !== nextDirection) flush();
    drag.pendingX = x;
  };

  const finish = (commitPending = false, unmount = false) => {
    if (commitPending) flush();
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
    const drag = gesture.current;
    gesture.current = null;
    if (!drag) return;
    if (drag.element.hasPointerCapture(drag.pointerId)) {
      drag.element.releasePointerCapture(drag.pointerId);
    }
    if (drag.active) {
      document.documentElement.classList.remove("is-scrubbing-number");
      if (!unmount) setScrubbing(false);
    }
  };
  const finishRef = useRef(finish);
  finishRef.current = finish;
  useEffect(() => {
    const stop = () => finishRef.current();
    const hide = () => { if (document.hidden) stop(); };
    window.addEventListener("blur", stop);
    document.addEventListener("visibilitychange", hide);
    return () => {
      window.removeEventListener("blur", stop);
      document.removeEventListener("visibilitychange", hide);
      finishRef.current(false, true);
    };
  }, []);
  useEffect(() => {
    if (disabled || readOnly) finishRef.current();
  }, [disabled, readOnly]);

  return <input
    {...props}
    type="number"
    value={value}
    disabled={disabled}
    readOnly={readOnly}
    className={`drag-number-input ${className}`.trim()}
    data-help={[help || title || props["aria-description"] || props["aria-label"], "Drag left or right to adjust in real time, or click to type."].filter(Boolean).join(" ")}
    aria-description={[props["aria-description"], "Drag left or right to adjust, or type a value."].filter(Boolean).join(" ")}
    data-scrubbing={scrubbing ? "true" : undefined}
    onChange={onChange ?? ((event) => onValueChange(Number(event.target.value)))}
    onPointerDown={(event) => {
      if (disabled || readOnly || event.button !== 0 || !event.isPrimary || gesture.current) return;
      suppressClick.current = false;
      const min = finite(props.min, -Infinity);
      const max = finite(props.max, Infinity);
      gesture.current = {
        element: event.currentTarget, pointerId: event.pointerId,
        x: event.clientX, y: event.clientY, lastX: event.clientX,
        pendingX: event.clientX, remainder: 0, active: false,
        value: Math.max(min, Math.min(max, finite(value, scrubFallbackValue))),
        step: Math.max(Number.EPSILON, finite(props.step, 1)), min, max,
      };
      event.currentTarget.setPointerCapture(event.pointerId);
    }}
    onPointerMove={(event) => {
      const drag = gesture.current;
      if (!drag || event.pointerId !== drag.pointerId) return;
      const dx = Math.abs(event.clientX - drag.x);
      const dy = Math.abs(event.clientY - drag.y);
      if (!drag.active) {
        if (event.pointerType === "touch" && dy >= 6 && dy > dx) {
          finish();
          return;
        }
        if (dx < 6) return;
        drag.active = true;
        suppressClick.current = true;
        setScrubbing(true);
        document.documentElement.classList.add("is-scrubbing-number");
      }
      event.preventDefault();
      updatePendingPoint(event.clientX);
      if (frame.current === null) frame.current = requestAnimationFrame(flush);
    }}
    onPointerUp={(event) => {
      const drag = gesture.current;
      if (event.pointerId !== drag?.pointerId) return;
      if (drag.active) {
        event.preventDefault();
        updatePendingPoint(event.clientX);
      }
      finish(true);
    }}
    onPointerCancel={() => finish()}
    onLostPointerCapture={() => finish()}
    onDragStart={(event) => event.preventDefault()}
    onClick={(event) => {
      if (suppressClick.current) {
        suppressClick.current = false;
        event.preventDefault();
        event.stopPropagation();
      } else props.onClick?.(event);
    }}
    onBlur={(event) => { finish(true); onBlur?.(event); }}
    onKeyDown={(event) => {
      if (gesture.current?.active) {
        finish();
        if (event.key === "Escape") {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
      }
      onKeyDown?.(event);
    }}
  />;
}
